import colors from 'theme/colors';
import { IDeal } from 'store/deals/types';

export const CLEAR_DEAL_EVALUATION = 'CLEAR_DEAL_EVALUATION';
export const GET_DEAL_EVALUATION = 'GET_DEAL_EVALUATION';
export const GET_DEAL_EVALUATION_SUCCESS = 'GET_DEAL_EVALUATION_SUCCESS';
export const GET_DEAL_EVALUATION_FAIL = 'GET_DEAL_EVALUATION_FAIL';

export const CLEAR_DEAL_IN_RESULTS = 'CLEAR_DEAL_IN_RESULTS';
export const SET_DEAL_IN_RESULTS = 'SET_DEAL_IN_RESULTS';

export const SET_IS_STALE = 'SET_IS_STALE';
export const SET_FORECAST_TYPE = 'SET_FORECAST_TYPE';
export const SET_COST_FORMULA_TYPE = 'SET_COST_FORMULA_TYPE';
export const SET_EVALUATION_UNIT = 'SET_EVALUATION_UNIT';
export const SET_CURRENT_EVALUATION_UNIT = 'SET_CURRENT_EVALUATION_UNIT';
export const SET_SELECTED_FORMULA_INDEX = 'SET_SELECTED_FORMULA_INDEX';

export enum EViewType {
  PROBABILITY = 'PROBABILITY',
  LINE = 'LINE',
  TABLE = 'TABLE',
}

export enum EForecastType {
  P5 = 'p5',
  P10 = 'p10',
  P25 = 'p25',
  P50 = 'p50',
  P75 = 'p75',
  P90 = 'p90',
  P95 = 'p95',
  AVERAGE = 'ave',
}

export enum ECostFormulaParamType {
  ITP = 'itp',
  TRUE_COST = 'trueCost', // integrated
}

export const ECostFormulaParamTypeLabel = {
  [ECostFormulaParamType.ITP]: 'ITP',
  [ECostFormulaParamType.TRUE_COST]: 'Integrated',
};

export interface IForecastLine {
  data: IDataPoint[];
  color: string;
  name: EForecastType;
}

export interface IDataPoint {
  x: Date | null;
  y: TNullableFloat;
}

export const forecastLines = [
  {
    name: EForecastType.P5,
    color: colors.error,
  },
  {
    name: EForecastType.P10,
    color: colors.warning,
  },
  {
    name: EForecastType.P25,
    color: colors.yellow,
  },
  {
    name: EForecastType.P50,
    color: colors.success,
  },
  {
    name: EForecastType.AVERAGE,
    color: colors.celestial,
  },
  {
    name: EForecastType.P75,
    color: colors.yellow,
  },
  {
    name: EForecastType.P90,
    color: colors.warning,
  },
  {
    name: EForecastType.P95,
    color: colors.error,
  },
];

export const forecastLinesSimple = [
  {
    name: EForecastType.P5,
    color: colors.error,
  },
  {
    name: EForecastType.AVERAGE,
    color: colors.celestial,
  },
  {
    name: EForecastType.P95,
    color: colors.error,
  },
];

// History contains just the historical values for a marker/formula.
export type TNullableFloat = number | null;

export interface ISummaryHistory {
  x: string;
  y: TNullableFloat;
}

// Forecast contains all of the metrics we want for a statistical summary of a forecast and the
// labels for the columns of the matrix.
export interface ISummaryForecast {
  x: string;

  ave: TNullableFloat;
  p5: TNullableFloat;
  p25: TNullableFloat;
  p50: TNullableFloat;
  p75: TNullableFloat;
  p95: TNullableFloat;
}

// Formula stores the summarize results for the output of a formula
export interface ISummaryFormula {
  total: ISummaryForecast;
  history: ISummaryHistory[];
  forecast: ISummaryForecast[];
}

// Price stores the summarized results for each price (base price and all BATNAs) provided
// for a product.
export interface ISummaryPrice {
  description: string; // The user provided description of the price formula

  base: ISummaryFormula; // The raw price per unit determined by the formula
  sales: ISummaryFormula; // The price per unit that would be charged to the customer
  netback: ISummaryFormula; // The effective value per unit
  margin: ISummaryFormula | null; // Netback - Manufacturing Cost

  marginAtRisk: TNullableFloat; // Ave margin - 5% margin
  costToServe: TNullableFloat; // cumulative value of all freight costs
  yield: TNullableFloat; // margin/freight cost (sometimes refered to as Cost to Serve Yield)
  cost_reimbursed: TNullableFloat;
  cost_freight: TNullableFloat;
  formula?: string;
}

// Option stores how much a single option on a product is worth
export interface ISummaryOption {
  ave: TNullableFloat;
  survival: TNullableFloat[];
}

// Options stores how much each option on a product is worth.
export interface ISummaryOptions {
  flex: ISummaryOption | null;
  cap: ISummaryOption | null;
  floor: ISummaryOption | null;
  push: ISummaryOption | null;
  pull: ISummaryOption | null;
  extension: ISummaryOption | null;
  earlyTerm: ISummaryOption | null;
  priceSwitch: ISummaryOption | null;
}

// Product stores the summarized results (for the line graph and table) for a product's data.
export interface ISummaryProduct {
  name: string;
  volume: number;
  headline: TNullableFloat;
  creditCost: TNullableFloat;

  contract: ISummaryPrice;
  withoutOpts: ISummaryPrice | null;
  mainBatna: ISummaryPrice;
  otherBatnas: ISummaryPrice[];

  options: ISummaryOptions;
  exposure?: TNullableFloat;
}

// DealOverview stores total and average values for all of the products within a deal.
export interface ISummaryDealOverview {
  volume: TNullableFloat;
  headline: TNullableFloat;
  upliftOverBatna: TNullableFloat;
  creditCost: TNullableFloat;
  marginAtRisk: TNullableFloat;
  yield: TNullableFloat;

  base: ISummaryForecast;
  sales: ISummaryForecast;
  netback: ISummaryForecast;
  margin: ISummaryForecast;

  cap: TNullableFloat;
  floor: TNullableFloat;
  push: TNullableFloat;
  pull: TNullableFloat;
  extension: TNullableFloat;
  earlyTerm: TNullableFloat;
}

// Deal stores the summarized results (for the line graph and table) for a deal's data.
export interface ISummaryDeal extends ISummaryDealOverview {
  interestRate: TNullableFloat;
  creditMult: TNullableFloat;
  // ISummaryDealOverview
  products: ISummaryProduct[] | null;
  maxExposure?: number;
}

export enum EForecastEvaluationType {
  SHELL = 'shell',
  IHS = 'ihs',
}

export const EForecastEvaluationTypeLabel = {
  [EForecastEvaluationType.SHELL]: 'Shell',
  [EForecastEvaluationType.IHS]: 'IHS',
};

export enum EEvaluationUnit {
  LBS = 'lbs',
  TON = 'ton',
}

export enum UNITS {
  DOLLAR = 'dollar',
  CENTS = 'cents',
}

export interface IDealEvaluationState {
  deal?: IDeal | null;
  error?: Error | null;
  [EForecastEvaluationType.IHS]?: ISummaryDeal | null;
  [EForecastEvaluationType.SHELL]?: ISummaryDeal | null;
  isStale: boolean;
  loading: boolean;
  selectedForecastType: EForecastEvaluationType;
  selectedUnitType: EEvaluationUnit;
  selectedCostFormulaType: ECostFormulaParamType;
  selectedFormulaIndex: number;
  selectedCurrentUnitType: string;
}

export interface IForecastDataRow {
  x: string;
  ave: TNullableFloat;
  p5: TNullableFloat;
  p10: TNullableFloat;
  p25: TNullableFloat;
  p50: TNullableFloat;
  p75: TNullableFloat;
  p90: TNullableFloat;
  p95: TNullableFloat;
  downside: TNullableFloat;
  upside: TNullableFloat;
  stdDev: TNullableFloat;
}

export interface IVisualItem {
  active: boolean;
  color: string;
  label: string;
  background?: string;
}

export interface ILineGraphItem extends IVisualItem {
  priceSummary: ISummaryPrice;
}

export interface ISurvivalPlotItem extends IVisualItem {
  optionSummary: ISummaryOption;
}

export interface IErrorBody {
  error: IErrorObject | null;
}

export interface IErrorObject {
  message: string;
}
